import { ArrowBack, Description, ThumbDown, ThumbUp } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, FormControl, Icon, IconButton, InputLabel, Stack, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import clsx from 'clsx';
import { createSubScore, getDisplayConfigFrom } from 'feature/report/AssessmentScore/SubScore/SubScoreLauncher';
import { Button } from 'global/components/Button';
import CheckpointForm from 'global/components/CheckpointForm';
import Confirm from 'global/components/confirm';
import useDebounce from 'global/hooks/useDebounce';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { first, get, isEqual, noop, set, size, sortBy } from 'global/lib/lodash';
import { getColorFromScore, useScreen } from 'global/styles';
import { getDisplayScoreLabel } from 'global/utils/getDisplayScore';
import { forwardRef, useEffect, useRef, useState } from 'react';
import sendData from 'redux/actions/sendData';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { useQueue } from '../../hooks/useQueue';
import Form from '../form';
import { Row } from '../row';
import ScoreIcon from '../ScoreIcon';
import { Text } from '../text';
import useStyles from './styles';

// This Component enables the creation and editing of an Instance. It is structured hierarchically:
// 1. The **SubScoreToggle** is the smallest unit and functions independently.
// 2. The **SubScoreForm** includes SubScoreToggle.
// 3. The **SubScoreModal** is a modal that contains both the form and the button group, encapsulating all elements.
// 4. The **SubScoreTypeDisplay** is a lightweight, read-only version of the SubScoreToggle.

export const SubScoreToggle = (props) => {
  const { subScoreValueOptions } = props;
  const [subScoreValue, setSubScoreValue] = useState(props.subScoreValue);
  const classes = useStyles();
  const theme = useTheme();

  const toggleButtonIcons = [
    <ThumbDown className={subScoreValueOptions[0].label ? classes.iconSpacing : undefined} />,
    <ThumbUp className={subScoreValueOptions[1].label ? classes.iconSpacing : undefined} />,
    <Description className={subScoreValueOptions[2].label ? classes.iconSpacing : undefined} />,
  ];

  const getColorFromValue = (val, buttonValue) => {
    // If the current val matches the button val, then it is selected.
    const isSelected = val === buttonValue;
    const color = getColorFromScore(val);
    return {
      backgroundColor: isSelected ? color : undefined,
      color: isSelected ? theme.palette.getContrastText(color) : undefined,
    };
  };

  const handleChange = (e, newValue) => {
    if (newValue !== null) {
      setSubScoreValue(newValue);
    }
  };

  useEffect(() => {
    props.onChange && props.onChange(subScoreValue);
  }, [subScoreValue]);

  return (
    <Box display="flex" justifyContent="flex-start">
      <ToggleButtonGroup disabled={props.disabled} exclusive value={subScoreValue} className={clsx(classes.roundedCorners)} onChange={handleChange}>
        {subScoreValueOptions.map((option, index) => (
          <ToggleButton key={index} value={option.value} style={getColorFromValue(subScoreValue, option.value)}>
            {toggleButtonIcons[index]}
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export const SubScoreForm = forwardRef((props, ref) => {
  const { filteredRealms, assessment, scorecard, updateScores, setIsDirty } = props;
  const pssvTag = get(props, 'pssvTag', {});
  const pssvChecklist = pssvTag?.pssvChecklist || props.pssvChecklist;
  const pssvCheckpoint = pssvTag?.pssvCheckpoint || props.pssvCheckpoint;
  const pssvSystem = pssvTag?.pssvSystem || props.pssvSystem;
  const [formState, setFormState] = useState(get(pssvTag, 'pssvSubScore', get(props, 'pssvCheckpoint', {})));
  const [initialFormState] = useState(get(pssvTag, 'pssvSubScore'));
  const [projectedScore, setProjectedScore] = useState(Number(pssvCheckpoint?.value));
  const [updateProjectedScore, setUpdateProjectedScore] = useState(false);
  const [canEditCheckpointForm, setCanEditCheckpointForm] = useState(false);
  const [isTradeSelectDirty, setIsTradeSelectDirty] = useState(false);
  const formRef = useRef();
  const debfs = useDebounce(formState, get(props, 'debounceTime', 1000));
  const submits = useRef(0);
  const [tradecheckloaded, settradecheckloaded] = useState(false);
  const [checkpointFormQueue, setCheckpointFormQueue] = useState([]);
  const { addToQueue } = useQueue({ onUpdate: props.updateScores, system: pssvSystem });
  const { isView1, isView2, hasSubScores } = getDisplayConfigFrom(scorecard, props);
  const checkpointId = get(pssvCheckpoint, 'checkpointId');
  const config = get(scorecard, 'config');
  const allowScoreEditing = !(config?.hasSubScores === true && config?.editScoreInfo === false && config?.subScoringClass !== null);
  // If onFormReady exists, it means we're in checklist edit mode, and changes are submitted incrementally
  const onFormReady = get(props, 'onFormReady');
  const isContinuousEdit = !!onFormReady;
  const realm = get(props, 'realm', get(formState, 'realm'));
  const isEditMode = props.isEditMode || pssvTag?.isEditMode || false;

  const _setCheckpointFormQueue = (queue) => {
    setCheckpointFormQueue([queue]);
  };

  const subScore = get(pssvTag, 'pssvSubScore', get(pssvCheckpoint, '_subscore'));
  const subscoreScore = parseInt(get(formState, 'score', get(subScore, 'score', 0)));

  const getProjectedScore = async () => {
    const { data } = await sendData({ url: `/sub-scores/test-score`, method: 'post', data: submitSubscoreData });
    setProjectedScore(get(data, 'data.attributes.score'));
  };

  //This code is to notify/propagate changes to the parent component whenever the combined state of formState, Score, and tag changes.
  useEffect(() => {
    const hasAllVals = !!debfs.realm && !!pssvTag && debfs.score !== undefined && tradecheckloaded;
    if (hasAllVals) {
      submits.current += 1;
      props.onFormReady &&
        props.onFormReady({
          formState: debfs,
          submits: submits.current,
          submitSubScore: submitSubScore,
        });
    }
  }, [debfs, tradecheckloaded]);

  useEffect(() => {
    const _fs = { ...formState, pssvTag };
    let hasUnsavedChanges;
    if (!!initialFormState) {
      // SubScore Edit Mode
      hasUnsavedChanges =
        (!props.canWallterSubscore && formState.notes !== null && !isEqual(formState.notes, initialFormState.notes)) ||
        !isEqual(formState.images, initialFormState.images) ||
        !isEqual(subscoreScore, Number(initialFormState.score)) ||
        isTradeSelectDirty;

      // Update projected score based on score change; once clicked, it never goes back to false.
      setUpdateProjectedScore(true);
    } else {
      // SubScore Create Mode
      hasUnsavedChanges = (!props.canWallterSubscore && !!formState?.notes) || !formState?.images === 0 || !(subscoreScore === 0) || isTradeSelectDirty;
      setUpdateProjectedScore(true);
    }
    setIsDirty?.(!!hasUnsavedChanges);
    props.onFormState && props.onFormState(_fs);
  }, [formState, pssvTag]);

  useEffect(() => {
    if (submitSubscoreData?.data?.attributes?.checkpoint_id && updateProjectedScore) {
      getProjectedScore();
    }
  }, [subscoreScore, updateProjectedScore]);

  // const _pssvCheckpoint = pssvCheckpoint || get(pssvTag, 'pssvCheckpoint');
  const superset_id = get(pssvTag, 'pssvChecklist.supersetId');
  const classes = useStyles();
  const assessmentRealmId = get(assessment, 'data.realmId');

  // If there's a label for each option, apply it.
  const subScoreValueLabel = get(scorecard, 'config.subScoreValueLabel') || [];
  const createSubScoreOptions = () => {
    const subScoreValueLabels = [];
    subScoreValueLabels.push({ value: 0, label: subScoreValueLabel.find((vl) => parseInt(vl.value) === 0)?.label });
    subScoreValueLabels.push({
      value: 100,
      label: subScoreValueLabel.find((vl) => parseInt(vl.value) === 100)?.label,
    });
    subScoreValueLabels.push({
      value: -1,
      label: subScoreValueLabel.find((vl) => parseInt(vl.value) === -1)?.label || 'Note',
    });
    return subScoreValueLabels;
  };
  const subScoreValueOptions = createSubScoreOptions();

  const disabled = !pssvCheckpoint || !realm || !((get(formState, 'notes') || '').trim() || !!size(get(formState, 'images'))) || get(formState, 'images', []).find((i) => i.loading);

  const name = get(pssvCheckpoint, 'name', '');

  const hasId = get(subScore, 'id', null);
  const subScoreData = useRetrieveData(
    { url: `/sub-scores/${hasId}` },
    {
      waitFor: hasId,
      build: ['subScores'],
      transformData: (d) => first(d),
      params: {
        include: 'tradeTypes',
      },
    }
  );

  const [params, setParams] = useState([
    { url: `/checkpoints/${checkpointId}` },
    {
      useEffect: true,
      waitFor: checkpointId ? undefined : null,
      params: { include: 'scorecardOptions' },
      build: ['checkpoints'],
      transformData: (d) => get(d, '0'),
    },
  ]);
  const checkpointData = useRetrieveData(...params);

  useEffect(() => {
    if (checkpointId) {
      setParams((p) => [{ url: `/checkpoints/${checkpointId}` }, { ...p[1], waitFor: undefined }]);
    }
  }, [checkpointId]);

  const submitSubscoreData = {
    data: {
      type: 'sub-scores',
      id: get(subScore, 'id'),
      attributes: {
        superset_id: get(pssvCheckpoint, 'supersetId', '').toString(),
        assessment_id: get(props, 'assessment.data.id', '').toString(),
        scorecard_id: get(pssvCheckpoint, 'scorecardId', '').toString(),
        checklist_id: get(pssvCheckpoint, 'checklistId', '').toString(),
        checkpoint_id: get(pssvCheckpoint, 'checkpointId', '').toString(),
        scorecard_option_id: '',

        score: `${get(formState, 'score', 0)}`,
        criteria: get(pssvCheckpoint, 'criteria', ''),
        realm_id: get(formState, 'realm', props.realm?.id || undefined),
        notes: get(formState, 'notes', '') || '',
        images: get(formState, 'images', []),
      },
    },
  };

  const submitSubScore = async () => {
    const hasRealmId = get(formState, 'realm', props.realm?.id || undefined);

    const isNewRealm = !hasRealmId;
    if (isNewRealm) {
      const realmName = get(formState, 'realm.name', realm?.name);
      const result = await sendData(
        {
          url: `/realms`,
          method: 'post',
          data: {
            data: {
              type: 'realms',
              attributes: {
                name: realmName,
                tier: get(filteredRealms, 'realmFilter.tier', 'room'),
                parent_id: get(first(filteredRealms.data), 'parentId') || assessmentRealmId,
              },
            },
          },
        },
        { normalize: { build: ['realms'] } }
      );

      get(filteredRealms, 'update', () => 0)();
      // realm = first(result);
      props.setRealm && props.setRealm(first(result));
    }

    let data = submitSubscoreData;

    const tradeTypes = get(formState, 'tradeTypes');
    set(data, 'data.relationships.tradeTypes', {
      data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
    });

    await UploadFiles(data);
    return await createSubScore(data);
  };

  useEffect(() => {
    if (checkpointFormQueue.length > 0 && hasSubScores && isView2) {
      props.addToQueue ? props.addToQueue(first(checkpointFormQueue)) : addToQueue(first(checkpointFormQueue));
      setCheckpointFormQueue([]);
    }
  }, [checkpointFormQueue]);

  const handleFormSubmit = async () => {
    const result = await submitSubScore();

    // Update the subscore to the tree
    await Promise.all(result.map((s) => (updateScores ? updateScores(s) : noop())));

    // Update the checkpoint with the new checkpoint score.
    if (checkpointFormQueue.length > 0) {
      props.addToQueue ? await props.addToQueue(first(checkpointFormQueue)) : addToQueue(first(checkpointFormQueue));
    }

    setIsDirty && setIsDirty(false);
    props.onRightPanelClose?.();
    props.onClose && props.onClose(!hasId);
  };

  const handleEditCheckpointScore = () => {
    setCanEditCheckpointForm((prev) => !prev);
  };

  return (
    <div className={classes.bottomSpacing}>
      <Form
        loading={checkpointData.loading || (hasId && subScoreData.loading)}
        showAllScreens={true}
        onFormState={(fst) => setFormState((prev) => ({ ...prev, ...fst }))}
        ref={formRef}
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'subscoreType',
                component: () => (
                  <>
                    <Text type="title_large" className={clsx(classes.cpScoreHeader, classes.bottomSpacing)}>
                      <strong>
                        {get(pssvTag, 'pssvChecklist.name')}: {get(pssvTag, 'pssvCheckpoint.name', '')}
                      </strong>
                    </Text>
                    <FormControl className={clsx(classes.toggleFormControl)}>
                      <InputLabel className={classes.toggleLabel}>Instance Type</InputLabel>
                      <SubScoreToggle
                        subScoreValue={subscoreScore}
                        subScoreValueOptions={subScoreValueOptions}
                        disabled={!tradecheckloaded}
                        onChange={(f) =>
                          setFormState((fs) => ({
                            ...fs,
                            score: f,
                          }))
                        }
                      />
                    </FormControl>
                  </>
                ),
              },
              isEditMode && {
                id: 'realm',
                type: 'Select',
                data: () => (filteredRealms?.data || props.realms?.data || []).map((d) => ({ title: d.name, value: d.id })),
                defaultValue: () => get(formState, 'realm.id'),
                props: {
                  label: 'Location',
                },
              },
              {
                id: 'notes',
                type: 'TextField',
                defaultValue: () => get(formState, 'notes'),
                props: {
                  placeholder: 'Describe your observation.',
                  rows: 5,
                  multiline: true,
                },
              },
              {
                id: 'images',
                type: 'DropArea',
                defaultValue: () => sortBy(get(formState, 'images', []), 'orderColumn'),
                props: {
                  label: 'Images',
                  sortable: true,
                  shouldUpdateOnCaptionChange: true,
                  notAllowed: ['pssvTag'],
                  parentId: hasId,
                  uploadAllNew: true,
                  collection: 'sub-score-images',
                  imageSelect: {
                    assessment: get(props, 'assessment.data'),
                    superset_id: superset_id,
                    disabled: !superset_id,
                  },
                },
              },
              {
                id: 'referenceScore',
                component: () => (
                  <FormControl className={clsx(classes.toggleFormControl)}>
                    <InputLabel className={classes.toggleLabel}>Reference Score</InputLabel>
                    <Row spaceBetween dontCenter>
                      {canEditCheckpointForm ? (
                        <Box display="flex" flexDirection="row" alignItems="center" width={'100%'}>
                          <CheckpointForm
                            pssvCheckpoint={get(pssvCheckpoint, 'pssvParentCheckpoint', pssvCheckpoint)}
                            pssvChecklist={pssvChecklist}
                            assessmentId={get(assessment, 'data.id')}
                            assessment={assessment}
                            checkpointdata={checkpointData}
                            loading={checkpointData.loading}
                            scorecard={scorecard}
                            canEditInfo={hasSubScores ? isView1 : undefined}
                            hideInfoIcon={true}
                            setNeedUpdate={false}
                            isCourse={props.isCourse}
                            addToQueue={_setCheckpointFormQueue}
                            queue={checkpointFormQueue}
                            submitCompleted={submits.current}
                          />
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Text variant="body2" className={classes.cpScoreHeaderChild}>
                            {getDisplayScoreLabel(
                              {
                                checklistId: pssvCheckpoint?.checklistId,
                                supersetId: pssvCheckpoint?.supersetId,
                                value: projectedScore,
                              },
                              0,
                              scorecard?.config
                            )}
                          </Text>
                          <ScoreIcon score={projectedScore} />
                          <div>
                            {
                              <Text variant={'body1'}>
                                <strong>{name}</strong> {pssvCheckpoint?.criteria || ''}
                              </Text>
                            }
                          </div>
                        </Box>
                      )}

                      {allowScoreEditing && (
                        <IconButton onClick={handleEditCheckpointScore}>
                          <Icon>{canEditCheckpointForm ? 'close' : 'edit'}</Icon>
                        </IconButton>
                      )}
                    </Row>
                  </FormControl>
                ),
              },
              {
                id: 'tradeTypes',
                type: 'TradeSelect',
                defaultValue: () => get(subScoreData, 'data.tradeTypes', []),
                props: {
                  checklist: get(pssvTag, 'pssvChecklist'),
                  onChecklistTradesLoaded: () => setTimeout(() => settradecheckloaded(true), 500),
                  setIsTradeSelectDirty: setIsTradeSelectDirty,
                  skipDefaults: !!hasId,
                },
              },
            ].filter((f) => !!f),
          },
        ]}
      />
      {!isContinuousEdit && (
        <Button onClick={handleFormSubmit} variant="contained" color="secondary" className={classes.btn} disabled={disabled}>
          <Icon className={classes.iconSpacing}>check</Icon>Save
        </Button>
      )}
    </div>
  );
});

const SubScoreModal = (props) => {
  const { onClose, open, isDialog, pssvTag, isDirty, setIsDirty } = props;
  const classes = useStyles();
  const isEditMode = props.pssvTag?.isEditMode || false;
  const { isMobile } = useScreen();
  const [conf, setConf] = useState({});
  const elementId = get(pssvTag, 'pssvSubScore.id', get(pssvTag, 'pssvCheckpoint.id'));

  const handleClose = () => {
    if (isDirty) {
      setConf({
        title: 'Unsaved changes',
        content: 'You have unsaved data, are you sure you want to close?',
        onOk: () => {
          setConf({});
          props.onRightPanelClose && props.onRightPanelClose();
          setIsDirty(false);
          onClose && onClose();
        },
        onCancel: () => {
          setConf({});
        },
      });
    } else {
      props.onRightPanelClose && props.onRightPanelClose();
      onClose && onClose();
    }
  };

  return (
    <>
      {isDialog ? (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile} className={classes.dialogText}>
          <DialogTitle>
            <Stack direction="row" width={'100%'} alignItems={'center'} spacing={2}>
              <Box display="flex" className={classes.justifyStart}>
                <IconButton onClick={handleClose}>
                  <ArrowBack />
                </IconButton>
              </Box>

              <Box display="flex" className={classes.justifyStart}>
                <Text type="title_large">{isEditMode ? 'Edit Instance' : 'Create Instance'}</Text>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <SubScoreForm key={elementId} {...props} isDirty={isDirty} setIsDirty={setIsDirty} isEditMode={isEditMode} />
          </DialogContent>
        </Dialog>
      ) : (
        open && (
          <>
            <Stack className={clsx(classes.titleContainer)} direction="row" width={'100%'} alignItems={'center'} spacing={2}>
              <Box display="flex" className={clsx(classes.justifyStart, classes.topSpacing)}>
                <IconButton onClick={handleClose} className={classes.noPaddingLeft}>
                  <ArrowBack />
                </IconButton>
              </Box>

              <Box display="flex" className={clsx(classes.justifyStart, classes.topSpacing)}>
                <Text type="title_large">{isEditMode ? 'Edit Instance' : 'Create Instance'}</Text>
              </Box>
            </Stack>
            <Box className={classes.subScoreFormPadding} sx={{ flex: 1, overflow: 'auto' }}>
              <SubScoreForm key={elementId} {...props} isDirty={isDirty} setIsDirty={setIsDirty} isEditMode={isEditMode} />
            </Box>
          </>
        )
      )}
      <Confirm {...conf} />
    </>
  );
};

export default SubScoreModal;
